import Decimal from 'decimal.js';
import { renderToBytes } from '@jasy/pdf';
import { computeInvoice } from '../node_modules/@jasy/zugferd/dist/compute.js';
import { defaultInvoiceTemplate } from '../node_modules/@jasy/zugferd/dist/template.js';
import { facturxXmp } from '../node_modules/@jasy/zugferd/dist/xmp.js';
import { makeFormatters, resolveLabels } from '../node_modules/@jasy/zugferd/dist/i18n.js';
import { toCII } from '../node_modules/@jasy/zugferd/dist/cii.js';
import type { Invoice, InvoiceLine } from '../node_modules/@jasy/zugferd/dist/invoice.js';
import { fontRegular, fontBold, fontItalic, fontBoldItalic, iccProfile } from './generated/assets';

const BASIC_GUIDELINE = 'urn:cen.eu:en16931:2017#compliant#urn:factur-x.eu:1p0:basic';
const EN_GUIDELINE = 'urn:cen.eu:en16931:2017';
const QDT_NAMESPACE = 'urn:un:unece:uncefact:data:standard:QualifiedDataType:100';
const MAX_LINES = 50;

type DocumentKind = 'invoice' | 'invoice-correction' | 'credit' | 'credit-correction';
type CreditMode = 'commercial' | 'self-billed';
type VatCategoryCode = 'S' | 'Z' | 'E' | 'AE' | 'K' | 'G' | 'O';
type TaxTreatmentId =
  | 'S19'
  | 'S7'
  | 'Z0'
  | 'AE'
  | 'E-KLEIN'
  | 'E-79-C'
  | 'E-132'
  | 'E-135-1'
  | 'E-143'
  | 'E-144'
  | 'E-146-1E'
  | 'E-148'
  | 'E-151'
  | 'E-153'
  | 'E-159'
  | 'E-309'
  | 'E-D'
  | 'E-F'
  | 'E-I'
  | 'E-J'
  | 'E-CUSTOM'
  | 'K'
  | 'G'
  | 'O';

type TaxTreatment = {
  id: TaxTreatmentId;
  label: string;
  category: VatCategoryCode;
  ratePercent?: number;
  exemptionText?: string;
  exemptionCode?: string;
  customReason?: boolean;
};

type VatExemptionReason = { text?: string; code?: string };
type InvoiceWithTaxReasons = Invoice & {
  vatExemptionReasons?: Partial<Record<VatCategoryCode, VatExemptionReason>>;
};

const TAX_TREATMENTS: Record<TaxTreatmentId, TaxTreatment> = {
  S19: { id: 'S19', label: '19 % Regelsteuersatz', category: 'S', ratePercent: 19 },
  S7: { id: 'S7', label: '7 % ermäßigter Steuersatz', category: 'S', ratePercent: 7 },
  Z0: { id: 'Z0', label: '0 % Nullsteuersatz', category: 'Z', ratePercent: 0 },
  AE: {
    id: 'AE', label: 'Reverse Charge – Steuerschuldnerschaft des Leistungsempfängers',
    category: 'AE', ratePercent: 0,
    exemptionText: 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge).',
    exemptionCode: 'VATEX-EU-AE',
  },
  'E-KLEIN': {
    id: 'E-KLEIN', label: 'Steuerbefreit – Kleinunternehmerregelung § 19 UStG',
    category: 'E', ratePercent: 0,
    exemptionText: 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.',
  },
  'E-79-C': {
    id: 'E-79-C', label: 'Nicht in Bemessungsgrundlage – Auslagenersatz Art. 79 Buchst. c MwStSystRL',
    category: 'E', ratePercent: 0,
    exemptionText: 'Nicht in der Bemessungsgrundlage enthaltener Auslagenersatz nach Artikel 79 Buchstabe c der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-79-C',
  },
  'E-132': {
    id: 'E-132', label: 'Steuerbefreit – Art. 132 MwStSystRL (bestimmte Gemeinwohlleistungen)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 132 der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-132',
  },
  'E-135-1': {
    id: 'E-135-1', label: 'Steuerbefreit – Art. 135 Abs. 1 MwStSystRL (u. a. Finanz-/Versicherungsumsätze)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 135 Absatz 1 der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-135-1',
  },
  'E-143': {
    id: 'E-143', label: 'Steuerbefreit – Art. 143 MwStSystRL (Einfuhr)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 143 der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-143',
  },
  'E-144': {
    id: 'E-144', label: 'Steuerbefreit – Art. 144 MwStSystRL (Leistungen im Zusammenhang mit Einfuhr)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 144 der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-144',
  },
  'E-146-1E': {
    id: 'E-146-1E', label: 'Steuerbefreit – Art. 146 Abs. 1 Buchst. e MwStSystRL (Leistungen im Zusammenhang mit Ausfuhr)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 146 Absatz 1 Buchstabe e der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-146-1E',
  },
  'E-148': {
    id: 'E-148', label: 'Steuerbefreit – Art. 148 MwStSystRL (internationaler Verkehr)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 148 der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-148',
  },
  'E-151': {
    id: 'E-151', label: 'Steuerbefreit – Art. 151 MwStSystRL (internationale Einrichtungen)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 151 der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-151',
  },
  'E-153': {
    id: 'E-153', label: 'Steuerbefreit – Art. 153 MwStSystRL (Vermittlungsleistungen)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 153 der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-153',
  },
  'E-159': {
    id: 'E-159', label: 'Steuerbefreit – Art. 159 MwStSystRL (Zoll-/Steuerlager)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 159 der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-159',
  },
  'E-309': {
    id: 'E-309', label: 'Steuerbefreit – Art. 309 MwStSystRL (Reiseleistungen außerhalb EU)',
    category: 'E', ratePercent: 0,
    exemptionText: 'Steuerbefreiung nach Artikel 309 der Richtlinie 2006/112/EG.',
    exemptionCode: 'VATEX-EU-309',
  },
  'E-D': {
    id: 'E-D', label: 'Sonderregelung – innergemeinschaftlicher Erwerb gebrauchter Fahrzeuge',
    category: 'E', ratePercent: 0,
    exemptionText: 'Innergemeinschaftlicher Erwerb eines gebrauchten Fahrzeugs nach der einschlägigen Sonderregelung.',
    exemptionCode: 'VATEX-EU-D',
  },
  'E-F': {
    id: 'E-F', label: 'Differenzbesteuerung – Gebrauchtgegenstände',
    category: 'E', ratePercent: 0,
    exemptionText: 'Differenzbesteuerung für Gebrauchtgegenstände.',
    exemptionCode: 'VATEX-EU-F',
  },
  'E-I': {
    id: 'E-I', label: 'Differenzbesteuerung – Kunstgegenstände',
    category: 'E', ratePercent: 0,
    exemptionText: 'Differenzbesteuerung für Kunstgegenstände.',
    exemptionCode: 'VATEX-EU-I',
  },
  'E-J': {
    id: 'E-J', label: 'Differenzbesteuerung – Sammlungsstücke und Antiquitäten',
    category: 'E', ratePercent: 0,
    exemptionText: 'Differenzbesteuerung für Sammlungsstücke und Antiquitäten.',
    exemptionCode: 'VATEX-EU-J',
  },
  'E-CUSTOM': {
    id: 'E-CUSTOM', label: 'Steuerbefreit – sonstiger Grund',
    category: 'E', ratePercent: 0, customReason: true,
  },
  K: {
    id: 'K', label: 'Innergemeinschaftliche Lieferung', category: 'K', ratePercent: 0,
    exemptionText: 'Steuerfreie innergemeinschaftliche Lieferung.', exemptionCode: 'VATEX-EU-IC',
  },
  G: {
    id: 'G', label: 'Ausfuhrlieferung außerhalb der EU', category: 'G', ratePercent: 0,
    exemptionText: 'Steuerfreie Ausfuhrlieferung außerhalb der EU.', exemptionCode: 'VATEX-EU-G',
  },
  O: {
    id: 'O', label: 'Nicht steuerbar / nicht der Umsatzsteuer unterliegend', category: 'O',
    exemptionText: 'Nicht der Umsatzsteuer unterliegend.', exemptionCode: 'VATEX-EU-O',
  },
};

const EU_COUNTRIES = new Set([
  'AT','BE','BG','HR','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK',
]);

type DocumentMeta = {
  code: 261 | 380 | 381 | 384 | 389;
  title: string;
  numberLabel: string;
  info: string;
  showReference: boolean;
  referenceRequired: boolean;
  reasonRequired: boolean;
  selfBilling: boolean;
  dueDateRequired: boolean;
  dueDateLabel: string;
  amountLabel: string;
  dueAmountLabel: string;
  paymentHeading: string;
  buttonLabel: string;
  filenameSuffix: string;
};

type PrecedingReference = {
  number: string;
  date: string;
};

const form = document.querySelector<HTMLFormElement>('#invoice-form')!;
const errors = document.querySelector<HTMLDivElement>('#errors')!;
const generateButton = document.querySelector<HTMLButtonElement>('#generate')!;
const xmlButton = document.querySelector<HTMLButtonElement>('#download-xml')!;
const addLineButton = document.querySelector<HTMLButtonElement>('#add-line')!;
const lineItems = document.querySelector<HTMLDivElement>('#line-items')!;
const lineTemplate = document.querySelector<HTMLTemplateElement>('#line-item-template')!;
const documentKindSelect = document.querySelector<HTMLSelectElement>('#document-kind')!;
const creditModeSelect = document.querySelector<HTMLSelectElement>('#credit-mode')!;
const creditModeField = document.querySelector<HTMLElement>('#credit-mode-field')!;
const creditModeLabel = document.querySelector<HTMLElement>('#credit-mode-label')!;
const referenceFields = document.querySelector<HTMLFieldSetElement>('#reference-fields')!;
const documentTypeInfo = document.querySelector<HTMLDivElement>('#document-type-info')!;
const typeCodeBadge = document.querySelector<HTMLElement>('#type-code-badge')!;
const numberLabel = document.querySelector<HTMLElement>('#number-label')!;
const documentDataHeading = document.querySelector<HTMLElement>('#document-data-heading')!;
const paymentHeading = document.querySelector<HTMLElement>('#payment-heading')!;
const grandTotalLabel = document.querySelector<HTMLElement>('#grand-total-label')!;
const dueDateInput = form.elements.namedItem('dueDate') as HTMLInputElement;
const dueDateField = document.querySelector<HTMLElement>('#due-date-field')!;
const sellerHeading = document.querySelector<HTMLElement>('#seller-heading')!;
const buyerHeading = document.querySelector<HTMLElement>('#buyer-heading')!;
const sellerRoleNote = sellerHeading.nextElementSibling as HTMLElement;
const buyerRoleNote = buyerHeading.nextElementSibling as HTMLElement;
let lastXml = '';
let lastXmlFilename = 'factur-x.xml';

function dataUriBytes(uri: string): Uint8Array {
  const comma = uri.indexOf(',');
  const raw = atob(uri.slice(comma + 1));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function value(name: string): string {
  const element = form.elements.namedItem(name);
  if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) {
    return element.value.trim();
  }
  return '';
}


function decimal(raw: string): Decimal {
  const normalized = raw.trim().replace(',', '.');
  return new Decimal(normalized || 0);
}

function money(amount: Decimal): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount.toNumber());
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character] ?? character);
}

function escapeXml(text: string): string {
  return text.replace(/[<>&'"]/g, (character) => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;',
  })[character] ?? character);
}

function date102(date: string): string {
  return date.replaceAll('-', '');
}

function currentDocumentMeta(): DocumentMeta {
  const kind = documentKindSelect.value as DocumentKind;
  const creditMode = creditModeSelect.value as CreditMode;

  if (kind === 'invoice') {
    return {
      code: 380,
      title: 'Rechnung',
      numberLabel: 'Rechnungsnummer',
      info: 'Reguläre Handelsrechnung. Die Leistung wird vom Lieferanten gegenüber dem Kunden abgerechnet.',
      showReference: false,
      referenceRequired: false,
      reasonRequired: false,
      selfBilling: false,
      dueDateRequired: true,
      dueDateLabel: 'Fälligkeitsdatum',
      amountLabel: 'Rechnungsbetrag',
      dueAmountLabel: 'Zahlbetrag',
      paymentHeading: 'Zahlung',
      buttonLabel: 'ZUGFeRD-Rechnung erstellen',
      filenameSuffix: 'Rechnung',
    };
  }

  if (kind === 'invoice-correction') {
    return {
      code: 384,
      title: 'Rechnungskorrektur',
      numberLabel: 'Korrekturbelegnummer',
      info: 'Berichtigte Rechnung mit eigener Belegnummer. Der vorausgegangene Beleg wird als BG-3 mit BT-25 und BT-26 referenziert.',
      showReference: true,
      referenceRequired: true,
      reasonRequired: true,
      selfBilling: false,
      dueDateRequired: true,
      dueDateLabel: 'Fälligkeitsdatum',
      amountLabel: 'Korrekturbetrag',
      dueAmountLabel: 'Zahlbetrag',
      paymentHeading: 'Zahlung',
      buttonLabel: 'ZUGFeRD-Rechnungskorrektur erstellen',
      filenameSuffix: 'Rechnungskorrektur',
    };
  }

  if (kind === 'credit' && creditMode === 'commercial') {
    return {
      code: 381,
      title: 'Kaufmännische Gutschrift / Storno',
      numberLabel: 'Gutschriftnummer',
      info: 'Kaufmännische Gutschrift beziehungsweise Storno durch den Lieferanten. Die Beträge werden positiv ausgegeben; die Gutschriftswirkung ergibt sich aus BT-3 Code 381. Ein Bezug auf einen früheren Beleg ist optional.',
      showReference: true,
      referenceRequired: false,
      reasonRequired: false,
      selfBilling: false,
      dueDateRequired: false,
      dueDateLabel: 'Auszahlungsdatum (optional)',
      amountLabel: 'Gutschriftbetrag',
      dueAmountLabel: 'Gutschriftbetrag',
      paymentHeading: 'Auszahlung / Verrechnung',
      buttonLabel: 'ZUGFeRD-Gutschrift erstellen',
      filenameSuffix: 'Gutschrift',
    };
  }

  if (kind === 'credit' && creditMode === 'self-billed') {
    return {
      code: 389,
      title: 'Gutschrift (Selbstabrechnung)',
      numberLabel: 'Gutschriftnummer',
      info: 'Umsatzsteuerliche Gutschrift im Selbstabrechnungsverfahren: Der Leistungsempfänger stellt den Beleg für den Leistungserbringer aus. Im XML bleibt der Leistungserbringer der Verkäufer (BG-4), der Leistungsempfänger der Käufer (BG-7).',
      showReference: false,
      referenceRequired: false,
      reasonRequired: false,
      selfBilling: true,
      dueDateRequired: true,
      dueDateLabel: 'Fälligkeitsdatum',
      amountLabel: 'Gutschriftbetrag',
      dueAmountLabel: 'Gutschriftbetrag',
      paymentHeading: 'Zahlung',
      buttonLabel: 'ZUGFeRD-Selbstabrechnung erstellen',
      filenameSuffix: 'Selbstabrechnung',
    };
  }

  if (kind === 'credit-correction' && creditMode === 'commercial') {
    return {
      code: 384,
      title: 'Korrektur einer kaufmännischen Gutschrift',
      numberLabel: 'Korrekturbelegnummer',
      info: 'Berichtigung einer bereits ausgestellten kaufmännischen Gutschrift. Der vorausgegangene Beleg wird in BG-3 referenziert; BT-3 wird als berichtigter Beleg mit Code 384 ausgegeben.',
      showReference: true,
      referenceRequired: true,
      reasonRequired: true,
      selfBilling: false,
      dueDateRequired: false,
      dueDateLabel: 'Auszahlungsdatum (optional)',
      amountLabel: 'Gutschriftkorrekturbetrag',
      dueAmountLabel: 'Gutschriftkorrekturbetrag',
      paymentHeading: 'Auszahlung / Verrechnung',
      buttonLabel: 'ZUGFeRD-Gutschriftkorrektur erstellen',
      filenameSuffix: 'Gutschriftkorrektur',
    };
  }

  return {
    code: 261,
    title: 'Gutschriftkorrektur (Selbstabrechnung)',
    numberLabel: 'Korrekturbelegnummer',
    info: 'Gutschriftkorrektur im Selbstabrechnungsverfahren. BT-3 wird mit Code 261 „Self billed credit note“ ausgegeben; der vorausgegangene Selbstabrechnungsbeleg wird in BG-3 referenziert. Die Akzeptanz dieses Codes sollte mit dem Empfänger abgestimmt werden.',
    showReference: true,
    referenceRequired: true,
    reasonRequired: true,
    selfBilling: true,
    dueDateRequired: false,
    dueDateLabel: 'Auszahlungsdatum (optional)',
    amountLabel: 'Gutschriftkorrekturbetrag',
    dueAmountLabel: 'Gutschriftkorrekturbetrag',
    paymentHeading: 'Auszahlung / Verrechnung',
    buttonLabel: 'ZUGFeRD-Selbstabrechnungskorrektur erstellen',
    filenameSuffix: 'Selbstabrechnungskorrektur',
  };
}
function setRequired(element: HTMLInputElement, required: boolean): void {
  element.required = required;
  const marker = element.closest('label')?.querySelector<HTMLElement>('.required-marker');
  if (marker) marker.hidden = !required;
}

function updateDocumentTypeUi(): void {
  const kind = documentKindSelect.value as DocumentKind;
  const meta = currentDocumentMeta();
  const hasCreditVariant = kind === 'credit' || kind === 'credit-correction';
  creditModeField.hidden = !hasCreditVariant;
  creditModeLabel.textContent = kind === 'credit-correction' ? 'Art der Gutschriftkorrektur' : 'Art der Gutschrift';
  creditModeSelect.required = hasCreditVariant;

  referenceFields.hidden = !meta.showReference;
  referenceFields.classList.toggle('optional-reference', meta.showReference && !meta.referenceRequired);
  const precedingNumber = form.elements.namedItem('precedingNumber') as HTMLInputElement;
  const precedingDate = form.elements.namedItem('precedingDate') as HTMLInputElement;
  const correctionReason = form.elements.namedItem('correctionReason') as HTMLInputElement;
  setRequired(precedingNumber, meta.referenceRequired);
  setRequired(precedingDate, meta.referenceRequired);
  setRequired(correctionReason, meta.reasonRequired);
  const referenceHelp = referenceFields.querySelector<HTMLElement>('.field-help');
  if (referenceHelp) {
    referenceHelp.textContent = meta.referenceRequired
      ? 'Pflichtangaben: Sie werden als BG-3 mit BT-25 und BT-26 in die XML-Datei geschrieben.'
      : 'Optionaler Bezug: Sobald Nummer oder Datum eingegeben wird, müssen beide Angaben vollständig sein.';
  }

  documentTypeInfo.textContent = meta.info;
  documentTypeInfo.classList.toggle('self-billing', meta.selfBilling);
  typeCodeBadge.textContent = `BT-3: ${meta.code}`;
  numberLabel.textContent = meta.numberLabel;
  documentDataHeading.textContent = `${meta.title} – Belegdaten`;
  paymentHeading.textContent = meta.paymentHeading;
  grandTotalLabel.textContent = meta.amountLabel;
  generateButton.textContent = meta.buttonLabel;

  sellerHeading.textContent = meta.selfBilling ? 'Leistungserbringer / XML-Verkäufer' : 'Lieferant / Leistungserbringer';
  buyerHeading.textContent = meta.selfBilling ? 'Leistungsempfänger / Belegaussteller' : 'Kunde / Leistungsempfänger';
  sellerRoleNote.textContent = 'Diese Partei wird in der XML-Datei als Verkäufer beziehungsweise Lieferant (BG-4) ausgegeben.';
  buyerRoleNote.textContent = meta.selfBilling
    ? 'Diese Partei stellt den Selbstabrechnungsbeleg aus und wird in der XML-Datei als Käufer beziehungsweise Kunde (BG-7) ausgegeben.'
    : 'Diese Partei wird in der XML-Datei als Käufer beziehungsweise Kunde (BG-7) ausgegeben.';

  setRequired(dueDateInput, meta.dueDateRequired);
  const dueLabelText = dueDateField.querySelector<HTMLElement>('.label-text');
  if (dueLabelText) dueLabelText.textContent = meta.dueDateLabel;
  updateTotals();
}

function lineCards(): HTMLElement[] {
  return Array.from(lineItems.querySelectorAll<HTMLElement>('[data-line-item]'));
}

function lineField(card: HTMLElement, field: string): HTMLInputElement | HTMLSelectElement {
  const element = card.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-field="${field}"]`);
  if (!element) throw new Error(`Positionsfeld ${field} fehlt.`);
  return element;
}

function taxTreatment(card: HTMLElement): TaxTreatment {
  const id = lineField(card, 'taxTreatment').value as TaxTreatmentId;
  const treatment = TAX_TREATMENTS[id];
  if (!treatment) throw new Error(`Unbekannte umsatzsteuerliche Behandlung: ${id}`);
  return treatment;
}

function taxReason(card: HTMLElement): VatExemptionReason | undefined {
  const treatment = taxTreatment(card);
  if (!['E', 'AE', 'K', 'G', 'O'].includes(treatment.category)) return undefined;
  const customText = lineField(card, 'customTaxReason').value.trim();
  return {
    text: treatment.customReason ? customText : treatment.exemptionText,
    code: treatment.exemptionCode,
  };
}

function syncTaxReasonField(card: HTMLElement): void {
  const treatment = taxTreatment(card);
  const wrapper = card.querySelector<HTMLElement>('[data-custom-tax-reason]')!;
  const input = lineField(card, 'customTaxReason') as HTMLInputElement;
  wrapper.hidden = !treatment.customReason;
  input.required = Boolean(treatment.customReason);
  if (!treatment.customReason) input.value = '';
}

function collectVatExemptionReasons(): Partial<Record<VatCategoryCode, VatExemptionReason>> {
  const reasons: Partial<Record<VatCategoryCode, VatExemptionReason>> = {};
  for (const card of lineCards()) {
    const treatment = taxTreatment(card);
    const reason = taxReason(card);
    if (reason) reasons[treatment.category] = reason;
  }
  return reasons;
}

function renumberLines(): void {
  const cards = lineCards();
  cards.forEach((card, index) => {
    card.querySelector<HTMLElement>('.line-number')!.textContent = String(index + 1);
    const remove = card.querySelector<HTMLButtonElement>('.remove-line')!;
    remove.disabled = cards.length === 1;
    remove.setAttribute('aria-label', `Position ${index + 1} entfernen`);
    syncTaxReasonField(card);
  });
  addLineButton.disabled = cards.length >= MAX_LINES;
}

function addLine(): void {
  if (lineCards().length >= MAX_LINES) return;
  const fragment = lineTemplate.content.cloneNode(true) as DocumentFragment;
  lineItems.append(fragment);
  renumberLines();
  updateTotals();
  const newest = lineCards().at(-1);
  newest?.querySelector<HTMLInputElement>('[data-field="name"]')?.focus();
}

function removeLine(card: HTMLElement): void {
  if (lineCards().length <= 1) return;
  card.remove();
  renumberLines();
  updateTotals();
}

function updateTotals(): void {
  let net = new Decimal(0);
  let taxTotal = new Decimal(0);
  const breakdown = new Map<string, { treatment: TaxTreatment; basis: Decimal; tax: Decimal }>();

  for (const card of lineCards()) {
    try {
      const quantity = decimal(lineField(card, 'quantity').value);
      const unitPrice = decimal(lineField(card, 'unitPrice').value);
      const lineNet = quantity.mul(unitPrice).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
      const treatment = taxTreatment(card);
      const lineTax = treatment.category === 'S'
        ? lineNet.mul(treatment.ratePercent ?? 0).div(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
        : new Decimal(0);
      card.querySelector<HTMLElement>('[data-line-total]')!.textContent = money(lineNet);
      net = net.plus(lineNet);
      taxTotal = taxTotal.plus(lineTax);
      const current = breakdown.get(treatment.id) ?? { treatment, basis: new Decimal(0), tax: new Decimal(0) };
      current.basis = current.basis.plus(lineNet);
      current.tax = current.tax.plus(lineTax);
      breakdown.set(treatment.id, current);
    } catch {
      card.querySelector<HTMLElement>('[data-line-total]')!.textContent = '–';
    }
  }

  document.querySelector('#net-total')!.textContent = money(net);
  const taxBreakdown = document.querySelector<HTMLElement>('#tax-breakdown')!;
  taxBreakdown.innerHTML = [...breakdown.values()].map(({ treatment, basis, tax }) =>
    `<div><span>${escapeHtml(treatment.label)} · Bemessungsgrundlage ${escapeHtml(money(basis))}</span><strong>${escapeHtml(money(tax))}</strong></div>`,
  ).join('');
  document.querySelector('#grand-total')!.textContent = money(net.plus(taxTotal));
}

function collectLines(): InvoiceLine[] {
  return lineCards().map((card, index) => {
    const quantity = decimal(lineField(card, 'quantity').value).toNumber();
    const netUnitPrice = decimal(lineField(card, 'unitPrice').value).toNumber();
    const treatment = taxTreatment(card);
    const vat = treatment.category === 'O'
      ? { category: 'O' as const }
      : { category: treatment.category, ratePercent: treatment.ratePercent ?? 0 };
    return {
      id: String(index + 1),
      name: lineField(card, 'name').value.trim(),
      description: lineField(card, 'description').value.trim() || undefined,
      quantity,
      unit: lineField(card, 'unit').value,
      netUnitPrice,
      vat: vat as InvoiceLine['vat'],
    };
  });
}

function buildNotes(meta: DocumentMeta): string[] | undefined {
  const notes: string[] = [];
  if (meta.selfBilling) {
    notes.push('Gutschrift');
    notes.push(`Ausgestellt durch den Leistungsempfänger ${value('buyerName')} im Selbstabrechnungsverfahren für den Leistungserbringer ${value('sellerName')}.`);
  } else if (meta.code === 381) {
    notes.push(`Kaufmännische Gutschrift / Storno zum Beleg ${value('precedingNumber')}.`);
  } else if (meta.title.startsWith('Korrektur einer kaufmännischen Gutschrift')) {
    notes.push(`Korrektur der kaufmännischen Gutschrift ${value('precedingNumber')}.`);
  }

  if (meta.showReference && value('correctionReason')) {
    notes.push(`Grund: ${value('correctionReason')}`);
  }
  const taxReasonNotes = new Set<string>();
  for (const card of lineCards()) {
    const treatment = taxTreatment(card);
    const reason = taxReason(card);
    if (reason?.text) taxReasonNotes.add(`${treatment.label}: ${reason.text}`);
  }
  for (const note of taxReasonNotes) notes.push(`Steuerhinweis: ${note}`);
  if (value('note')) notes.push(value('note'));
  return notes.length ? notes : undefined;
}


function buildInvoice(meta: DocumentMeta): InvoiceWithTaxReasons {
  const iban = value('iban').replace(/\s+/g, '').toUpperCase();
  const paymentTerms = value('paymentTerms');
  const payment = iban || paymentTerms
    ? {
        meansCode: iban ? '58' : undefined,
        iban: iban || undefined,
        bic: value('bic').replace(/\s+/g, '').toUpperCase() || undefined,
        accountName: value('sellerName') || undefined,
        reference: value('number'),
        terms: paymentTerms || undefined,
      }
    : undefined;

  return {
    number: value('number'),
    issueDate: value('issueDate'),
    dueDate: value('dueDate') || undefined,
    type: meta.code as Invoice['type'],
    currency: 'EUR',
    buyerReference: value('buyerReference') || undefined,
    notes: buildNotes(meta),
    seller: {
      name: value('sellerName'),
      vatId: value('sellerVatId'),
      contact: value('contactDepartment') || value('contactEmail')
        ? { name: value('contactDepartment') || undefined, email: value('contactEmail') || undefined }
        : undefined,
      address: {
        line1: value('sellerStreet'),
        postCode: value('sellerPostCode'),
        city: value('sellerCity'),
        country: value('sellerCountry').toUpperCase(),
      },
    },
    buyer: {
      name: value('buyerName'),
      vatId: value('buyerVatId') || undefined,
      address: {
        line1: value('buyerStreet'),
        postCode: value('buyerPostCode'),
        city: value('buyerCity'),
        country: value('buyerCountry').toUpperCase(),
      },
    },
    delivery: { date: value('serviceDate') },
    lines: collectLines(),
    vatExemptionReasons: collectVatExemptionReasons(),
    payment,
  };
}

function precedingReference(meta: DocumentMeta): PrecedingReference | undefined {
  if (!meta.showReference) return undefined;
  const number = value('precedingNumber');
  const date = value('precedingDate');
  if (!number && !date) return undefined;
  return { number, date };
}

function validate(meta: DocumentMeta): string[] {
  const messages: string[] = [];
  if (!form.reportValidity()) messages.push('Bitte füllen Sie alle markierten Pflichtfelder korrekt aus.');
  if (value('dueDate') && value('dueDate') < value('issueDate')) messages.push('Das Fälligkeitsdatum darf nicht vor dem Belegdatum liegen.');
  const iban = value('iban').replace(/\s+/g, '').toUpperCase();
  if (iban && !/^DE\d{20}$/.test(iban)) messages.push('Die eingegebene deutsche IBAN hat kein plausibles Format.');
  const bic = value('bic').replace(/\s+/g, '').toUpperCase();
  if (bic && !/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(bic)) messages.push('Der BIC muss 8 oder 11 Zeichen enthalten.');

  if (meta.showReference) {
    const referenceNumber = value('precedingNumber');
    const referenceDate = value('precedingDate');
    if (meta.referenceRequired && !referenceNumber) messages.push('Die Nummer des vorausgegangenen Belegs ist erforderlich.');
    if (meta.referenceRequired && !referenceDate) messages.push('Das Datum des vorausgegangenen Belegs ist erforderlich.');
    if (!meta.referenceRequired && Boolean(referenceNumber) !== Boolean(referenceDate)) {
      messages.push('Beim optionalen Belegbezug müssen Nummer und Datum gemeinsam angegeben werden.');
    }
    if (meta.reasonRequired && !value('correctionReason')) messages.push('Bitte geben Sie einen Korrekturgrund an.');
    if (referenceDate && referenceDate > value('issueDate')) messages.push('Das Datum des vorausgegangenen Belegs darf nicht nach dem neuen Belegdatum liegen.');
  }

  const cards = lineCards();
  if (!cards.length) messages.push('Mindestens eine Position ist erforderlich.');
  cards.forEach((card, index) => {
    const position = index + 1;
    try {
      if (!lineField(card, 'name').value.trim()) messages.push(`Position ${position}: Leistung oder Artikel fehlt.`);
      if (decimal(lineField(card, 'quantity').value).lte(0)) messages.push(`Position ${position}: Die Menge muss größer als null sein.`);
      if (decimal(lineField(card, 'unitPrice').value).lt(0)) messages.push(`Position ${position}: Der Nettopreis darf nicht negativ sein.`);
      const treatment = taxTreatment(card);
      if (treatment.customReason && !lineField(card, 'customTaxReason').value.trim()) {
        messages.push(`Position ${position}: Bitte geben Sie den individuellen Steuerbefreiungsgrund an.`);
      }
    } catch {
      messages.push(`Position ${position}: Menge oder Nettopreis ist keine gültige Zahl.`);
    }
  });

  const sellerCountry = value('sellerCountry').toUpperCase();
  const buyerCountry = value('buyerCountry').toUpperCase();
  const categories = new Set(lineCards().map((card) => taxTreatment(card).category));
  if (categories.has('O') && categories.size > 1) {
    messages.push('Nicht steuerbare Umsätze (Kategorie O) dürfen in diesem Beleg nicht mit anderen Umsatzsteuerkategorien kombiniert werden.');
  }
  if (categories.has('AE')) {
    if (!value('sellerVatId')) messages.push('Für Reverse Charge ist die USt-IdNr. des Lieferanten erforderlich.');
    if (!value('buyerVatId')) messages.push('Für Reverse Charge ist die USt-IdNr. des Leistungsempfängers erforderlich.');
  }
  if (categories.has('K')) {
    if (!value('sellerVatId') || !value('buyerVatId')) messages.push('Für eine innergemeinschaftliche Lieferung sind die USt-IdNrn. beider Parteien erforderlich.');
    if (!EU_COUNTRIES.has(sellerCountry) || !EU_COUNTRIES.has(buyerCountry) || sellerCountry === buyerCountry) {
      messages.push('Für eine innergemeinschaftliche Lieferung müssen Lieferant und Kunde in unterschiedlichen EU-Mitgliedstaaten ansässig sein.');
    }
  }
  if (categories.has('G')) {
    if (!EU_COUNTRIES.has(sellerCountry) || EU_COUNTRIES.has(buyerCountry)) {
      messages.push('Für eine Ausfuhrlieferung muss der Lieferant in der EU und der Kunde außerhalb der EU ansässig sein.');
    }
  }

  const eReasons = new Set(lineCards()
    .filter((card) => taxTreatment(card).category === 'E')
    .map((card) => JSON.stringify(taxReason(card))));
  if (eReasons.size > 1) {
    messages.push('Mehrere unterschiedliche Steuerbefreiungsgründe der Kategorie E können in einem Beleg derzeit nicht kombiniert werden.');
  }
  return [...new Set(messages)];
}

function applyTaxReasonXml(xml: string, reasons: Partial<Record<VatCategoryCode, VatExemptionReason>>): string {
  return xml.replace(/<ram:ApplicableTradeTax>([\s\S]*?)<\/ram:ApplicableTradeTax>/g, (whole, inner: string) => {
    const category = inner.match(/<ram:CategoryCode>([^<]+)<\/ram:CategoryCode>/)?.[1] as VatCategoryCode | undefined;
    if (!category) return whole;
    const isHeaderBreakdown = inner.includes('<ram:CalculatedAmount>') && inner.includes('<ram:BasisAmount>');
    let patched = inner
      .replace(/<ram:ExemptionReason>[\s\S]*?<\/ram:ExemptionReason>/g, '')
      .replace(/<ram:ExemptionReasonCode>[\s\S]*?<\/ram:ExemptionReasonCode>/g, '');
    if (category === 'O') {
      patched = patched.replace(/<ram:RateApplicablePercent>[^<]*<\/ram:RateApplicablePercent>/g, '');
    }
    if (isHeaderBreakdown) {
      const reason = reasons[category];
      if (reason?.text) {
        patched = patched.replace('</ram:TypeCode>', `</ram:TypeCode><ram:ExemptionReason>${escapeXml(reason.text)}</ram:ExemptionReason>`);
      }
      if (reason?.code) {
        patched = patched.replace('</ram:CategoryCode>', `</ram:CategoryCode><ram:ExemptionReasonCode>${escapeXml(reason.code)}</ram:ExemptionReasonCode>`);
      }
    }
    return `<ram:ApplicableTradeTax>${patched}</ram:ApplicableTradeTax>`;
  });
}

function basicXml(invoice: InvoiceWithTaxReasons, meta: DocumentMeta, reference?: PrecedingReference): string {
  const computed = computeInvoice(invoice);
  let xml = toCII(invoice, computed, 'en16931');
  if (!xml.includes(EN_GUIDELINE)) throw new Error('Unerwartete Profilkennung des XML-Generators.');
  xml = xml.replace(EN_GUIDELINE, BASIC_GUIDELINE);

  // Die verwendete Bibliotheksversion typisiert nur die häufigsten Codes. Laufzeitseitig wird BT-3
  // dennoch aus dem Belegmodell ausgegeben; dieser Abgleich stellt den ausgewählten Code zusätzlich sicher.
  xml = xml.replace(
    /(<rsm:ExchangedDocument>[\s\S]*?<ram:TypeCode>)\d+(<\/ram:TypeCode>)/,
    `$1${meta.code}$2`,
  );

  // Im BASIC-Profil wird die optionale Positionsbeschreibung als IncludedNote des Zeilendokuments geführt.
  xml = xml.replace(
    /<ram:AssociatedDocumentLineDocument>([\s\S]*?)<\/ram:AssociatedDocumentLineDocument>([\s\S]*?)<ram:SpecifiedTradeProduct>([\s\S]*?)<ram:Description>([\s\S]*?)<\/ram:Description>([\s\S]*?)<\/ram:SpecifiedTradeProduct>/g,
    '<ram:AssociatedDocumentLineDocument>$1<ram:IncludedNote><ram:Content>$4</ram:Content></ram:IncludedNote></ram:AssociatedDocumentLineDocument>$2<ram:SpecifiedTradeProduct>$3$5</ram:SpecifiedTradeProduct>',
  );

  xml = applyTaxReasonXml(xml, invoice.vatExemptionReasons ?? {});

  if (reference) {
    if (!xml.includes('xmlns:qdt=')) {
      xml = xml.replace(
        '<rsm:CrossIndustryInvoice ',
        `<rsm:CrossIndustryInvoice xmlns:qdt="${QDT_NAMESPACE}" `,
      );
    }
    const referenceXml =
      `<ram:InvoiceReferencedDocument>` +
      `<ram:IssuerAssignedID>${escapeXml(reference.number)}</ram:IssuerAssignedID>` +
      `<ram:FormattedIssueDateTime><qdt:DateTimeString format="102">${date102(reference.date)}</qdt:DateTimeString></ram:FormattedIssueDateTime>` +
      `</ram:InvoiceReferencedDocument>`;
    const totalsEnd = '</ram:SpecifiedTradeSettlementHeaderMonetarySummation>';
    if (!xml.includes(totalsEnd)) throw new Error('Die Referenz auf den vorausgegangenen Beleg konnte nicht eingefügt werden.');
    xml = xml.replace(totalsEnd, `${totalsEnd}${referenceXml}`);
  }

  return xml;
}

function pdfDisplayInvoice(invoice: Invoice, meta: DocumentMeta): Invoice {
  if (!meta.selfBilling) return invoice;

  const roleNotes = [
    `Leistungserbringer / XML-Verkäufer: ${invoice.seller.name}${invoice.seller.vatId ? `, USt-IdNr. ${invoice.seller.vatId}` : ''}.`,
    `Leistungsempfänger / Belegaussteller: ${invoice.buyer.name}${invoice.buyer.vatId ? `, USt-IdNr. ${invoice.buyer.vatId}` : ''}.`,
  ];

  // Für die Sichtkomponente steht bei Selbstabrechnung der ausstellende Leistungsempfänger im Briefkopf
  // und der Leistungserbringer im Empfängerfeld. Die XML-Rollen bleiben unverändert und normgerecht.
  return {
    ...invoice,
    seller: invoice.buyer as Invoice['seller'],
    buyer: invoice.seller as Invoice['buyer'],
    notes: [...roleNotes, ...(invoice.notes ?? [])],
  };
}

async function generatePdf(invoice: Invoice, meta: DocumentMeta, xml: string): Promise<Uint8Array> {
  const displayInvoice = pdfDisplayInvoice(invoice, meta);
  const computed = computeInvoice(displayInvoice);
  const labels = resolveLabels('de', {
    invoice: meta.title,
    invoiceNumber: meta.numberLabel,
    invoiceDate: 'Belegdatum',
    deliveryDate: 'Leistungsdatum',
    grandTotal: meta.amountLabel,
    amountDue: meta.dueAmountLabel,
    payment: meta.paymentHeading,
  });
  const fmt = makeFormatters('de', 'EUR');
  const doc = defaultInvoiceTemplate(displayInvoice, computed, labels, fmt);
  const fonts = {
    Helvetica: {
      normal: dataUriBytes(fontRegular),
      bold: dataUriBytes(fontBold),
      italic: dataUriBytes(fontItalic),
      boldItalic: dataUriBytes(fontBoldItalic),
    },
  };
  const author = meta.selfBilling ? invoice.buyer.name : invoice.seller.name;
  const title = `${meta.title} ${invoice.number}`;
  return renderToBytes(doc, {
    fonts,
    standardFonts: false,
    compress: true,
    attachments: [{
      name: 'factur-x.xml',
      data: new TextEncoder().encode(xml),
      relationship: 'Alternative',
      mimeType: 'text/xml',
      description: `ZUGFeRD 2.5 BASIC – ${meta.title}`,
    }],
    xmp: facturxXmp({ title, author, conformanceLevel: 'BASIC' }),
    outputIntent: dataUriBytes(iccProfile),
    pdfVersion: '1.7',
    documentId: true,
    title,
    lang: 'de-DE',
  });
}

function download(bytes: Uint8Array, filename: string, type: string): void {
  const blob = new Blob([bytes], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

form.addEventListener('input', (event) => {
  if (event.target instanceof HTMLElement) {
    const card = event.target.closest<HTMLElement>('[data-line-item]');
    if (card) {
      if (event.target.matches('[data-field="taxTreatment"]')) syncTaxReasonField(card);
      updateTotals();
    }
  }
});

documentKindSelect.addEventListener('change', updateDocumentTypeUi);
creditModeSelect.addEventListener('change', updateDocumentTypeUi);
addLineButton.addEventListener('click', addLine);
lineItems.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  const removeButton = target.closest<HTMLButtonElement>('.remove-line');
  if (!removeButton) return;
  const card = removeButton.closest<HTMLElement>('[data-line-item]');
  if (card) removeLine(card);
});

form.addEventListener('submit', async (event) => {
  event.preventDefault();
  const meta = currentDocumentMeta();
  const messages = validate(meta);
  if (messages.length) {
    errors.innerHTML = `<strong>Bitte korrigieren:</strong><ul>${messages.map((message) => `<li>${escapeHtml(message)}</li>`).join('')}</ul>`;
    errors.hidden = false;
    errors.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  errors.hidden = true;
  generateButton.disabled = true;
  generateButton.textContent = `${meta.title} wird lokal erstellt …`;
  try {
    const invoice = buildInvoice(meta);
    lastXml = basicXml(invoice, meta, precedingReference(meta));
    const bytes = await generatePdf(invoice, meta, lastXml);
    const safe = invoice.number.replace(/[^A-Za-z0-9._-]+/g, '_');
    download(bytes, `${safe}-${meta.filenameSuffix}-ZUGFeRD-BASIC.pdf`, 'application/pdf');
    lastXmlFilename = `${safe}-factur-x.xml`;
    xmlButton.disabled = false;
  } catch (error) {
    errors.textContent = `Der Beleg konnte nicht erstellt werden: ${error instanceof Error ? error.message : String(error)}`;
    errors.hidden = false;
  } finally {
    generateButton.disabled = false;
    generateButton.textContent = meta.buttonLabel;
  }
});

xmlButton.addEventListener('click', () => {
  if (lastXml) download(new TextEncoder().encode(lastXml), lastXmlFilename, 'application/xml');
});

renumberLines();
updateDocumentTypeUi();
updateTotals();
